import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, switchMap, throwError, map, catchError } from 'rxjs';
import { AuthService } from '@app/services/auth.service';
import { environment } from '@env/environment';

@Injectable()
export class AddHeadersInterceptor implements HttpInterceptor {
  private readonly DEFAULT_ORG_ID = '00000000-0000-0000-0000-000000000000';

  constructor(private authService: AuthService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let tokenObservable: Observable<string | null>;

    if (req.url.startsWith(environment.complianceApiUrl)) {
      tokenObservable = from(this.authService.acquireComplianceApiToken());
    } else if (req.url.startsWith(environment.dataApiUrl)) {
      tokenObservable = from(this.authService.acquireDataApiToken());
    } else {
      console.warn(`No matching token scope for URL: ${req.url}`);
      return next.handle(req);
    }

    return tokenObservable.pipe(
      switchMap((token) => {
        let clonedRequest = req;
        let headers = req.headers;

        if (token) {
          headers = headers.set('Authorization', `Bearer ${token}`);
        }

        // Add Organization-Id header for data API requests
        if (req.url.startsWith(environment.dataApiUrl)) {
          let organizationId = this.DEFAULT_ORG_ID; 

          const selectedOrgString = localStorage.getItem('selectedOrganization');
          if (selectedOrgString) {
            try {
              const selectedOrg = JSON.parse(selectedOrgString);
              if (selectedOrg && selectedOrg.organizationId) {
                organizationId = selectedOrg.organizationId;
              } else {
                console.warn(
                  'Organization ID not found in localStorage item "selectedOrganization", using default'
                );
              }
            } catch (error) {
              console.error(
                'Error parsing selectedOrganization from localStorage, using default:',
                error
              );
            }
          } else {
            console.warn('localStorage item "selectedOrganization" not found, using default.');
          }

          headers = headers.set('Organization-Id', organizationId);
        }

        clonedRequest = req.clone({ headers });

        return next.handle(clonedRequest).pipe(
          map((event) => {
            if (event instanceof HttpResponse) {
              // Don't try to parse as JSON if the request explicitly asked for text or blob
              if (req.responseType === 'text' || req.responseType === 'blob') {
                return event;
              }
              if (typeof event.body === 'string') {
                try {
                  const cleanBodyString = this.cleanJsonString(event.body);
                  const cleanBody = JSON.parse(cleanBodyString);
                  return event.clone({ body: cleanBody });
                } catch (error) {
                  console.error('Error parsing JSON response:', error);
                  throw new HttpErrorResponse({
                    error: 'Error parsing JSON response after cleaning',
                    status: event.status,
                    statusText: event.statusText,
                    url: event.url ?? undefined,
                  });
                }
              }
            }
            return event;
          }),
          catchError((error: HttpErrorResponse) => {
            console.error('HTTP Error:', error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  private cleanJsonString(jsonString: string): string {
    return jsonString.replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ').replace(/\\n/g, ' ');
  }
}
